// Purpose: three-state theme toggle (auto / light / dark). Auto follows
// the OS preference via prefers-color-scheme; light + dark are explicit
// user overrides persisted to localStorage under 'ast-theme'.
//
// Visual modes:
//   mode="button"   — single cycling button (Monitor/Sun/Moon icon).
//                     Used in the top nav on /login + the external
//                     shell, and anywhere space is tight.
//   mode="segmented" — three-button segmented control. Used in the
//                     workspace/admin sidebar user card so the active
//                     choice is obvious without hover.
//
// Boot script in index.html sets the initial data-theme before React
// mounts; this component then keeps it in sync.

import { useCallback, useEffect, useState } from 'react';
import { MoonIcon, MonitorIcon, SunIcon } from '../icons/index.tsx';

const STORAGE_KEY = 'ast-theme';

type Mode = 'auto' | 'light' | 'dark';
type Resolved = 'light' | 'dark';

function readStoredMode(): Mode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'auto') return raw;
  } catch {
    /* storage unavailable */
  }
  return 'auto';
}

function resolveMode(mode: Mode): Resolved {
  if (mode === 'light' || mode === 'dark') return mode;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

function applyResolvedTheme(resolved: Resolved): void {
  document.documentElement.setAttribute('data-theme', resolved);
}

export interface ThemeToggleProps {
  /** Visual form. Defaults to single-button cycle. */
  mode?: 'button' | 'segmented';
  className?: string;
}

export function ThemeToggle({ mode: visualMode = 'button', className }: ThemeToggleProps) {
  const [mode, setMode] = useState<Mode>(readStoredMode);

  const pickMode = useCallback((next: Mode): void => {
    setMode(next);
    try {
      if (next === 'auto') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      /* storage unavailable; accept the loss */
    }
    applyResolvedTheme(resolveMode(next));
  }, []);

  // Keep in sync with OS preference while the user is on auto.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (ev: MediaQueryListEvent): void => {
      if (readStoredMode() !== 'auto') return;
      applyResolvedTheme(ev.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (visualMode === 'segmented') {
    return (
      <div
        role="radiogroup"
        aria-label="Theme"
        className={[
          'inline-flex items-center p-0.5 gap-0.5',
          'bg-bg-tertiary border border-border-subtle rounded-md',
          className ?? '',
        ].join(' ')}
      >
        <SegButton current={mode} value="auto" onPick={pickMode} label="Auto">
          <MonitorIcon size={14} />
        </SegButton>
        <SegButton current={mode} value="light" onPick={pickMode} label="Light">
          <SunIcon size={14} />
        </SegButton>
        <SegButton current={mode} value="dark" onPick={pickMode} label="Dark">
          <MoonIcon size={14} />
        </SegButton>
      </div>
    );
  }

  // Single button: cycle auto → light → dark → auto.
  const next: Mode = mode === 'auto' ? 'light' : mode === 'light' ? 'dark' : 'auto';
  const Icon = mode === 'auto' ? MonitorIcon : mode === 'light' ? SunIcon : MoonIcon;
  const labels: Record<Mode, string> = {
    auto: 'Auto theme — follow system',
    light: 'Light theme',
    dark: 'Dark theme',
  };
  return (
    <button
      type="button"
      onClick={() => pickMode(next)}
      aria-label={`${labels[mode]}. Click to switch to ${labels[next].toLowerCase()}.`}
      title={labels[mode]}
      className={[
        'h-8 w-8 inline-flex items-center justify-center',
        'bg-transparent border border-border-subtle rounded-md',
        'text-fg-secondary cursor-pointer',
        'transition-colors duration-fast ease-out',
        'hover:border-border-strong hover:text-fg-primary',
        className ?? '',
      ].join(' ')}
    >
      <Icon size={16} />
    </button>
  );
}

function SegButton({
  current,
  value,
  onPick,
  label,
  children,
}: {
  current: Mode;
  value: Mode;
  onPick: (v: Mode) => void;
  label: string;
  children: React.ReactNode;
}) {
  const isActive = current === value;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      aria-label={label}
      onClick={() => onPick(value)}
      className={[
        'h-7 px-2 inline-flex items-center justify-center gap-1 rounded-[6px]',
        'transition-colors duration-fast ease-out cursor-pointer',
        isActive
          ? 'bg-surface-elevated text-fg-primary shadow-sm'
          : 'text-fg-secondary hover:text-fg-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
